import {Component, NgZone, OnInit, OnDestroy} from '@angular/core';
import {JsBridgeService} from "./services/js-bridge.service";
import {LocalStorageService} from "./services/local-storage.service";
import {GlobalService} from "./services/global.service";
import {PushContent} from "./shared/interfaces/pushContent";
import {NativeFileUpload} from "./shared/interfaces/nativeFileUpload";
import {Router, NavigationEnd, ActivatedRoute} from '@angular/router';
import {Title} from '@angular/platform-browser';
import {PushContentService} from "./services/push-content.service";
import {IonApp, IonRouterOutlet, ToastController} from "@ionic/angular/standalone";
import {filter, map, Subscription} from 'rxjs';

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss'],
    standalone: true,
    imports: [IonApp, IonRouterOutlet]
})
export class AppComponent implements OnInit, OnDestroy {

  routeAnnouncement = '';
  private routeSub?: Subscription;

  constructor(
    private _ngZone: NgZone,
    protected localStorageService: LocalStorageService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private pushContentService: PushContentService,
    private jsBridgeService: JsBridgeService,
    private globalService: GlobalService,
    private toastController: ToastController,
  ) {
    window.AppComponentRef = {
      zone: this._ngZone,
      component: this,
    };
  }

  ngOnInit(): void {
    window.onPushReceived = (pushContent) => this.onPushReceived(pushContent);
    window.onDeviceIdReceived = (deviceId) => this.onDeviceIdReceived(deviceId);
    window.setAsMobilePlatform = () => this.setAsMobilePlatform();
    window.setVersionAndPlatform = (vp) => this.setVersionAndPlatform(vp);
    window.openUrlFromUniversalLink = (vp) => this.openUrlFromUniversalLink(vp);
    window.onQrCodeValueReceived = (value) => this.onQrCodeValueReceived(value);
    window.onFileReceived = (data) => this.onFileReceived(data);
    window.onFileUploadError = (error) => this.onFileUploadError(error);

    this.localStorageService.isMobilePlatform = this.isWebView;

    this.routeSub = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => {
        let route = this.activatedRoute;
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route.snapshot.title;
      })
    ).subscribe(pageTitle => {
      const title = pageTitle ? `${pageTitle} - MensApp` : 'MensApp';
      this.titleService.setTitle(title);
      this.routeAnnouncement = pageTitle ? `Pagina: ${pageTitle}` : '';
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
  }

  openUrlFromUniversalLink(url: string): void {
    console.log('[openUrlFromUniversalLink] raw url:', url);
    console.log('[openUrlFromUniversalLink] IsInAngularZone:', NgZone.isInAngularZone());

    if (!url) {
      console.warn('[openUrlFromUniversalLink] invalid url');
      return;
    }

    const ref = window.AppComponentRef;
    if (!ref) {
      console.warn('[openUrlFromUniversalLink] AppComponentRef is undefined');
      return;
    }

    ref.zone.run(() => {
      try {
        const normalizedUrl = this.normalizeUniversalLink(url);
        console.log('[openUrlFromUniversalLink] navigating to:', normalizedUrl);

        this.router.navigateByUrl(normalizedUrl);
      } catch (err) {
        console.error('[openUrlFromUniversalLink] navigation error', err);
      }
    });
  }
  private normalizeUniversalLink(url: string): string {
    if (url.startsWith('/')) {
      return url;
    }
    try {
      const parsed = new URL(url);

      return parsed.pathname + parsed.search + parsed.hash;
    } catch {
      return '/' + url;
    }
  }


  onDeviceIdReceived(deviceId: string) {
    console.log('[onDeviceIdReceived] id:', deviceId);
    console.log('[onDeviceIdReceived] IsInAngularZone:', NgZone.isInAngularZone());

    window.AppComponentRef?.zone.run(() => {
      console.log('[onDeviceIdReceived] AppComponentRef run invoked.');
      window.AppComponentRef?.component.localStorageService.setDeviceId(deviceId);
    });
  }

  setAsMobilePlatform() {
    console.log('[setAsMobilePlatform]');

    const ref = window.AppComponentRef;
    if (!ref) {
      console.warn('[setAsMobilePlatform] AppComponentRef is undefined');
      return;
    }

    ref.zone.run(() => {
      console.log('[setAsMobilePlatform] AppComponentRef run invoked.');
      ref.component.localStorageService.isMobilePlatform = true;
    });
  }



  setVersionAndPlatform(versionAndPlatform: { version: string, platform: string }) {
    console.log('[setVersionAndPlatform]', JSON.stringify(versionAndPlatform));
    window.AppComponentRef?.zone.run(() => {
      this.globalService.appVersion = versionAndPlatform.version;
    });
  }

  onQrCodeValueReceived(value: string) {
    console.log('[onQrCodeValueReceived] value:', value);
    window.AppComponentRef?.zone.run(() => {
      this.globalService.scannerValue$.next(value);
    });
  }

  onFileReceived(data: NativeFileUpload) {
    console.log('[onFileReceived]', data.fileName, data.fileSize);
    window.AppComponentRef?.zone.run(() => {
      try {
        const binaryString = atob(data.file);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: data.fileType });
        const file = new File([blob], data.fileName, { type: data.fileType });
        this.globalService.nativeFileReceived$.next(file);
      } catch (e) {
        console.error('[onFileReceived] conversion error', e);
      }
    });
  }

  async onFileUploadError(error: string) {
    console.warn('[onFileUploadError]', error);
    window.AppComponentRef?.zone.run(async () => {
      const toast = await this.toastController.create({
        message: error,
        duration: 3000,
        position: 'bottom',
        color: 'danger',
      });
      await toast.present();
    });
  }

  /*
  MARK: FUNZIONI DI LETTURA SU PONTE JS
  */
  onPushReceived(pushContent: PushContent) {
    console.log('[onPushReceived] pushContent:', pushContent);
    console.log('[onPushReceived] IsInAngularZone:', NgZone.isInAngularZone());

    window.AppComponentRef?.zone.run(() => {
      console.log('[onPushReceived] AppComponentRef run invoked.');

      window.AppComponentRef?.component.pushContentService.gestisciNotifichePush(pushContent);
      window.AppComponentRef?.component.jsBridgeService.clearPushNotification();
    });
  }



  get isWebView(): boolean {
    const platformType = this.localStorageService.getPlatform();
    console.log(platformType, 'platformType')
    const isIosWebView = platformType?.toLowerCase() === 'IOS'.toLowerCase();
    const isAndroidWebView = platformType?.toLowerCase() === 'ANDROID'.toLowerCase();
    const isHuaweiWebView = platformType?.toLowerCase() === 'HUAWEI'.toLowerCase();

    return isAndroidWebView || isIosWebView || isHuaweiWebView;
  }

}
